import { quantile } from "@/utils/statistics";
import { close, forEachComponent, touchesBorder } from "./grid-mask";
import { CellHeights, Grid } from "./height-grid";

/** Size of the blocks of the ground model, in meters */
const GROUND_BLOCK = 1;
/** Radius of the ground model, larger than buildings seen from outside and than equipment */
const GROUND_RADIUS = 10;
/** The ground is this quantile of the block heights around, robust to basements and roofs */
const GROUND_QUANTILE = 0.2;
/** Lowest surfaces this high above the ground are roofs, canopies or ceilings without a floor under them */
const MAX_HEIGHT_ABOVE_GROUND = 3;
/** Raised surfaces enclosed by the floor up to this size are equipment tops, the floor under them counts */
const MAX_HIDDEN_FLOOR_M2 = 100;
/** Gaps narrower than about twice this size don't break the floor around equipment */
const ENCLOSURE_GAP = 0.4;
/** The lowest level extends this many cells into its walls and furniture */
const GROW_STEPS = 1;
/** Walls and furniture stand on the floor within this height */
const BASE_TOLERANCE = 0.15;

/**
 * Models the ground as a low quantile of the lowest surface heights around each block,
 * so it follows slopes and basements but not roofs
 *
 * @param grid The grid
 * @param lowest Height of the lowest surface of each cell, NaN where none
 * @returns a function giving the ground height at a cell
 */
function modelGround(
  { width, height, cellSize }: Grid,
  lowest: Float32Array,
): (cell: number) => number {
  const block = Math.max(1, Math.round(GROUND_BLOCK / cellSize));
  const blocksX = Math.ceil(width / block);
  const blocksZ = Math.ceil(height / block);
  const blockHeights = new Float32Array(blocksX * blocksZ).fill(NaN);
  const inBlock = new Float32Array(block * block);
  for (let br = 0; br < blocksZ; br++) {
    for (let bc = 0; bc < blocksX; bc++) {
      let count = 0;
      for (let r = br * block; r < Math.min(height, (br + 1) * block); r++) {
        for (let c = bc * block; c < Math.min(width, (bc + 1) * block); c++) {
          const value = lowest[r * width + c];
          if (!Number.isNaN(value)) inBlock[count++] = value;
        }
      }
      if (count) {
        blockHeights[br * blocksX + bc] = quantile(
          inBlock.subarray(0, count).sort(),
          0.5,
        );
      }
    }
  }

  const radius = Math.round(GROUND_RADIUS / (block * cellSize));
  const ground = new Float32Array(blocksX * blocksZ).fill(NaN);
  const around = new Float32Array((2 * radius + 1) ** 2);
  for (let br = 0; br < blocksZ; br++) {
    for (let bc = 0; bc < blocksX; bc++) {
      if (Number.isNaN(blockHeights[br * blocksX + bc])) continue;
      let count = 0;
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          const r = br + dr;
          const c = bc + dc;
          if (dr * dr + dc * dc > radius * radius) continue;
          if (r < 0 || c < 0 || r >= blocksZ || c >= blocksX) continue;
          const value = blockHeights[r * blocksX + c];
          if (!Number.isNaN(value)) around[count++] = value;
        }
      }
      ground[br * blocksX + bc] = quantile(
        around.subarray(0, count).sort(),
        GROUND_QUANTILE,
      );
    }
  }
  return (cell) =>
    ground[
      Math.floor(Math.floor(cell / width) / block) * blocksX +
        Math.floor((cell % width) / block)
    ];
}

/**
 * Finds the lowest walkable surface of each cell: floors, basements and outdoor ground.
 * Raised surfaces with no ground under them are left out, roofs, canopies and ceilings above unscanned floors,
 * unless the floor encloses them, as equipment hides the floor it stands on.
 *
 * @param grid The grid
 * @param layers The candidate surfaces of each cell
 * @param isHorizontal Which layers are horizontal surfaces
 * @returns the height of the lowest surface of each cell, NaN where none
 */
export function findLowestSurface(
  grid: Grid,
  layers: CellHeights,
  isHorizontal: Uint8Array,
): Float32Array {
  const { width, height } = grid;
  const lowest = new Float32Array(width * height).fill(NaN);
  for (let cell = 0; cell < width * height; cell++) {
    for (let k = layers.starts[cell]; k < layers.starts[cell + 1]; k++) {
      if (isHorizontal[k]) {
        lowest[cell] = layers.heights[k];
        break;
      }
    }
  }

  const groundAt = modelGround(grid, lowest);
  const isGround = new Uint8Array(width * height);
  const isRaised = new Uint8Array(width * height);
  lowest.forEach((value, cell) => {
    if (Number.isNaN(value)) return;
    if (value - groundAt(cell) > MAX_HEIGHT_ABOVE_GROUND) isRaised[cell] = 1;
    else isGround[cell] = 1;
  });
  const enclosed = close(
    { width, height, data: isGround },
    Math.max(1, Math.round(ENCLOSURE_GAP / grid.cellSize)),
  );
  forEachComponent(enclosed, 0, (cells) => {
    const isHidden =
      cells.length * grid.cellSize ** 2 <= MAX_HIDDEN_FLOOR_M2 &&
      !touchesBorder(cells, width, height);
    for (const cell of cells) {
      if (isRaised[cell] && !isHidden) lowest[cell] = NaN;
    }
  });
  return lowest;
}

/**
 * Extends the lowest level into the neighbor cells with points at its height, where walls and furniture stand on it
 *
 * @param grid The grid
 * @param cells The point heights by cell
 * @param lowest Height of the lowest surface of each cell, NaN where none, extended in place
 */
export function growToWalls(
  { width }: Grid,
  { starts, heights }: CellHeights,
  lowest: Float32Array,
): void {
  for (let step = 0; step < GROW_STEPS; step++) {
    const previous = lowest.slice();
    for (let cell = 0; cell < previous.length; cell++) {
      if (!Number.isNaN(previous[cell])) continue;
      const column = cell % width;
      const neighbors = [
        column > 0 ? cell - 1 : -1,
        column < width - 1 ? cell + 1 : -1,
        cell - width,
        cell + width,
      ];
      for (const neighbor of neighbors) {
        const level = previous[neighbor] ?? NaN;
        if (Number.isNaN(level)) continue;
        let hasBase = false;
        for (let k = starts[cell]; k < starts[cell + 1] && !hasBase; k++) {
          hasBase = Math.abs(heights[k] - level) <= BASE_TOLERANCE;
        }
        if (hasBase) {
          lowest[cell] = level;
          break;
        }
      }
    }
  }
}
